import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Fade,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Close,
  FileUpload as FileUploadIcon,
  Warning as WarningIcon,
  Replay,
  Block,
} from '@mui/icons-material';
import { UploadProgress } from '../types/File';

// Add CSS for shimmer animation
const shimmerKeyframes = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = shimmerKeyframes;
  document.head.appendChild(style);
}

interface FileUploadProps {
  onFilesUploaded: (files: File[]) => void;
  uploadProgress: UploadProgress[];
  onClearProgress: () => void;
  onRemoveProgressItem: (id: string) => void;
  onCancelUpload?: (id: string) => void;
  onRetryUpload?: (id: string) => void;
}

// File size threshold for warning (5MB)
const FILE_SIZE_WARNING_THRESHOLD = 5 * 1024 * 1024;

// Full-window drops bypass react-dropzone's own filtering, so validate against the same
// rules here. Mirrors the dropzone `accept` list and maxSize below.
const MAX_UPLOAD_SIZE = 50 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp',
  'mp4', 'avi', 'mov', 'wmv', 'webm',
  'mp3', 'wav', 'ogg', 'm4a',
  'pdf', 'txt', 'csv', 'zip', 'rar',
]);

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesUploaded,
  uploadProgress,
  onClearProgress,
  onRemoveProgressItem,
  onCancelUpload,
  onRetryUpload,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isWindowDragging, setIsWindowDragging] = useState(false);
  const [fileSizes, setFileSizes] = useState<{ [key: string]: number }>({});
  const [sizeWarningDialog, setSizeWarningDialog] = useState<{
    open: boolean;
    pendingFiles: File[];
    largeFiles: { name: string; size: number; isVideo: boolean }[];
  }>({
    open: false,
    pendingFiles: [],
    largeFiles: [],
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isVideoFile = (file: File) => {
    return file.type.startsWith('video/');
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError(null);
      
      // Store file sizes for display
      const sizes: { [key: string]: number } = {};
      acceptedFiles.forEach(file => {
        sizes[file.name] = file.size;
      });
      setFileSizes(sizes);
      
      // Check for files larger than 5MB
      const largeFiles = acceptedFiles
        .filter(file => file.size > FILE_SIZE_WARNING_THRESHOLD)
        .map(file => ({
          name: file.name,
          size: file.size,
          isVideo: isVideoFile(file),
        }));
      
      if (largeFiles.length > 0) {
        // Show warning dialog
        setSizeWarningDialog({
          open: true,
          pendingFiles: acceptedFiles,
          largeFiles,
        });
      } else {
        // No large files, proceed with upload
        onFilesUploaded(acceptedFiles);
      }
    },
    [onFilesUploaded]
  );

  // Files dropped anywhere on the window (not just on the dashed box). react-dropzone
  // doesn't see these, so filter by type/size here before handing them to onDrop.
  const handleWindowFiles = useCallback(
    (dropped: File[]) => {
      const accepted: File[] = [];
      let rejected = 0;
      dropped.forEach((file) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (file.size > MAX_UPLOAD_SIZE || !ACCEPTED_EXTENSIONS.has(ext)) rejected += 1;
        else accepted.push(file);
      });
      if (accepted.length > 0) onDrop(accepted);
      if (rejected > 0) {
        setError(`Skipped ${rejected} file${rejected !== 1 ? 's' : ''} (unsupported type or over 50 MB).`);
      }
    },
    [onDrop]
  );

  // Show a full-window overlay while files are dragged over the page, and accept a drop
  // anywhere. Depth counting avoids flicker as the pointer crosses child elements.
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setIsWindowDragging(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth -= 1;
      if (depth <= 0) {
        depth = 0;
        setIsWindowDragging(false);
      }
    };
    const onWindowDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setIsWindowDragging(false);
      const dropped = Array.from(e.dataTransfer?.files || []);
      if (dropped.length > 0) handleWindowFiles(dropped);
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onWindowDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onWindowDrop);
    };
  }, [handleWindowFiles]);

  const handleWarningConfirm = () => {
    // User chose to proceed with upload
    onFilesUploaded(sizeWarningDialog.pendingFiles);
    setSizeWarningDialog({ open: false, pendingFiles: [], largeFiles: [] });
  };

  const handleWarningCancel = () => {
    // User chose to cancel
    setSizeWarningDialog({ open: false, pendingFiles: [], largeFiles: [] });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // Keep drops on this dropzone from also bubbling to the window-level drop handler.
    noDragEventsBubbling: true,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'],
      'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.webm'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a'],
      'application/pdf': ['.pdf'],
      'text/*': ['.txt', '.csv'],
      'application/*': ['.zip', '.rar'],
    },
    maxSize: 50 * 1024 * 1024, // 50MB (matches the Pages Function MAX_FILE_SIZE)
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections[0]?.errors[0];
      let message = 'File upload failed';
      if (rejection?.code === 'file-too-large') {
        message = 'File is too large. The maximum size is 50 MB.';
      } else if (rejection?.code === 'file-invalid-type') {
        message = 'Unsupported file type. Allowed: images, videos, audio, PDFs, text files, and archives.';
      } else if (rejection?.message) {
        message = rejection.message;
      }
      setError(message);
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'cancelled':
        return <Block color="disabled" />;
      case 'uploading':
        return <FileUploadIcon color="primary" />;
      default:
        return <FileUploadIcon color="action" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Upload complete';
      case 'error':
        return 'Upload failed';
      case 'cancelled':
        return 'Cancelled';
      case 'uploading':
        return 'Uploading...';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'cancelled':
        return 'default';
      case 'uploading':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      {isWindowDragging && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            bgcolor: 'rgba(22, 20, 26, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            border: '3px dashed',
            borderColor: 'primary.main',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CloudUpload sx={{ fontSize: 80, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ color: '#fff' }}>
              Drop files to upload
            </Typography>
          </Box>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop files here...' : 'Drag & drop files here'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to select files
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Supports images, videos, PDFs, text files, and archives (max 50MB)
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'warning.main' }}>
          Recommended: Keep files under 5MB for optimal web performance
        </Typography>
      </Paper>

      {uploadProgress.length > 0 && (
        <Slide direction="up" in={uploadProgress.length > 0} mountOnEnter unmountOnExit>
          <Paper sx={{ mt: 2, p: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Upload Progress</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label={`${uploadProgress.filter(p => p.status === 'completed').length}/${uploadProgress.length} complete`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <IconButton onClick={onClearProgress} size="small">
                  <Close />
                </IconButton>
              </Box>
            </Box>
            
            <List sx={{ p: 0 }}>
              {uploadProgress.map((item, index) => (
                <Fade in={true} timeout={300 + index * 100} key={item.id}>
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: item.status === 'completed' ? 'success.50' : 
                               item.status === 'error' ? 'error.50' : 'background.paper',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        bgcolor: item.status === 'completed' ? 'success.100' : 
                                 item.status === 'error' ? 'error.100' : 'action.hover',
                        transform: 'translateY(-1px)',
                        boxShadow: 1,
                      }
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.fileName}
                              </Typography>
                              <Chip 
                                label={getStatusText(item.status)}
                                size="small"
                                color={getStatusColor(item.status) as any}
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {item.status === 'uploading' ? `${item.progress}% • ${formatFileSize(fileSizes[item.fileName] || 0)}` : 
                               item.status === 'completed' ? `Successfully uploaded • ${formatFileSize(fileSizes[item.fileName] || 0)}` :
                               item.status === 'error' ? 'Upload failed' : 'Ready to upload'}
                            </Typography>
                          }
                          sx={{ flex: 1 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {item.status === 'uploading' ? (
                            onCancelUpload && (
                              <Tooltip title="Cancel upload">
                                <IconButton
                                  onClick={() => onCancelUpload(item.id)}
                                  size="small"
                                  aria-label="Cancel upload"
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          ) : (
                            <>
                              {(item.status === 'error' || item.status === 'cancelled') && onRetryUpload && (
                                <Tooltip title="Retry upload">
                                  <IconButton
                                    onClick={() => onRetryUpload(item.id)}
                                    size="small"
                                    color="primary"
                                    aria-label="Retry upload"
                                  >
                                    <Replay fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove from list">
                                <IconButton
                                  onClick={() => onRemoveProgressItem(item.id)}
                                  size="small"
                                  aria-label="Remove from list"
                                  sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </Box>
                      
                      {item.status === 'uploading' && (
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={item.progress}
                            color="primary"
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                              }
                            }}
                          />
                        </Box>
                      )}
                      
                      {item.status === 'completed' && (
                        <Box sx={{ 
                          width: '100%', 
                          height: 6, 
                          bgcolor: 'success.main', 
                          borderRadius: 3,
                          opacity: 0.6,
                          position: 'relative',
                          overflow: 'hidden',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                            animation: 'shimmer 2s infinite',
                          }
                        }} />
                      )}
                      
                      {item.status === 'error' && (
                        <Box sx={{ 
                          width: '100%', 
                          height: 6, 
                          bgcolor: 'error.main', 
                          borderRadius: 3,
                          opacity: 0.6,
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '80%',
                            height: 2,
                            bgcolor: 'error.dark',
                            borderRadius: 1,
                          }
                        }} />
                      )}
                    </Box>
                  </ListItem>
                </Fade>
              ))}
            </List>
          </Paper>
        </Slide>
      )}

      {/* File Size Warning Dialog */}
      <Dialog
        open={sizeWarningDialog.open}
        onClose={handleWarningCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: '#1e1e24',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffc107' }}>
          <WarningIcon color="warning" />
          Large File Size Detected
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {sizeWarningDialog.largeFiles.length === 1 
              ? 'The following file exceeds the recommended 5MB limit for web assets:'
              : `${sizeWarningDialog.largeFiles.length} files exceed the recommended 5MB limit for web assets:`
            }
          </Typography>
          
          <Box sx={{ 
            bgcolor: 'rgba(255, 193, 7, 0.1)', 
            borderRadius: 1, 
            p: 2, 
            mb: 2,
            border: '1px solid rgba(255, 193, 7, 0.3)',
          }}>
            {sizeWarningDialog.largeFiles.map((file, index) => (
              <Typography key={index} variant="body2" sx={{ mb: index < sizeWarningDialog.largeFiles.length - 1 ? 1 : 0 }}>
                <strong>{file.name}</strong> — {formatFileSize(file.size)}
                {file.isVideo && (
                  <Chip 
                    label="Video" 
                    size="small" 
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                    color="info"
                  />
                )}
              </Typography>
            ))}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Why does this matter?</strong> Large files can slow down page load times and 
            increase bandwidth costs, negatively impacting user experience and SEO.
          </Typography>

          {sizeWarningDialog.largeFiles.some(f => f.isVideo) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Tip for videos:</strong> Consider using <strong>WebM</strong> format for better 
                compression while maintaining quality. WebM files are typically 30-50% smaller than MP4.
              </Typography>
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            You can still proceed with the upload, or cancel to optimize your files first.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={handleWarningCancel}
            variant="outlined"
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              '&:hover': {
                borderColor: '#00d4ff',
                backgroundColor: 'rgba(0, 212, 255, 0.1)',
              },
            }}
          >
            Cancel & Optimize
          </Button>
          <Button 
            onClick={handleWarningConfirm}
            variant="contained"
            color="warning"
            sx={{
              backgroundColor: '#ffc107',
              color: '#000000',
              '&:hover': {
                backgroundColor: '#ffca28',
              },
            }}
          >
            Upload Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};